"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { css } from "@/../styled-system/css";
import LoaderCircle from "@/assets/svg/loader-circle.svg";
import Input from "@/components/ui/Input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  ReactForm,
} from "@/components/ui/ReactForm";
import {
  TInterestFormSchema,
  interestFormSchema,
} from "@/types/InterestFormTypes/types";

import Button from "../ui/Button";
import { Checkbox } from "../ui/Checkbox";
import PhoneInput from "../ui/PhoneInput";
import Textarea from "../ui/Textarea";
import { toast } from "../ui/UseToast";

// import { Button } from "@/components/ui/button";

export default function InterestFormFinalNoAction() {
  //   const [state, formAction] = useFormState(onSubmitInterestForm, {
  //     message: "",
  //   });
  //   const { pending } = useFormStatus();
  const form = useForm<TInterestFormSchema>({
    resolver: zodResolver(interestFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      message: "",
      specialties: [],
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  // Effect to show toast when state.message is updated
  //   useEffect(() => {
  //     if (state?.message === "Message sent successfully") {
  //       toast({
  //         title: "Success",
  //         description: state.message,
  //       });
  //       reset();
  //     } else {
  //       toast({
  //         title: "Error",
  //         description: state.message,
  //         variant: "destructive",
  //       });
  //     }
  //   }, [reset, state.message]);

  const onSubmitForm = async (data: TInterestFormSchema) => {
    const response = await fetch("/api/about-us", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!responseData.ok) {
      toast({
        title: "Success",
        description: "Message sent successfully",
      });
      reset();
    } else {
      toast({
        title: "Error",
        description: "Message failed to send",
        variant: "destructive",
      });
    }
  };

  const specialtiesOptions = [
    "Website design",
    "UX design",
    "User research",
    "Content creation",
    "Strategy & consulting",
    "Other",
  ] as const;

  type Specialties = (typeof specialtiesOptions)[number];

  return (
    <ReactForm {...form}>
      {/* {state?.message !== "" && !state.issues && (
        <div className="text-red-500">{state.message}</div>
      )} */}
      {/* {state?.issues && (
        <div className="text-red-500">
          <ul>
            {state.issues.map((issue: any) => (
              <li key={issue} className="flex gap-1">
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )} */}
      <form
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "6",
          maxWidth: "480px",
          mx: "auto",
        })}
        onSubmit={handleSubmit(onSubmitForm)}
      >
        <div className={css({ sm: { display: "flex", gap: "8" } })}>
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem css={{ mb: "8", sm: { mb: "unset" } }}>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input
                    disabled={isSubmitting}
                    placeholder="First Name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input
                    disabled={isSubmitting}
                    placeholder="Last Name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  disabled={isSubmitting}
                  placeholder="e.g. stephenking@lorem.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <PhoneInput
                  disabled={isSubmitting}
                  placeholder="e.g. +1 234 567 890"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                {/* <Input placeholder="Message" {...field} /> */}
                <Textarea
                  disabled={isSubmitting}
                  placeholder="Message"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="specialties"
          render={({ field }) => (
            <FormItem css={{ gap: "4" }}>
              <FormLabel>What is your specialty?</FormLabel>
              <FormControl>
                <div
                  className={css({
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  })}
                >
                  {specialtiesOptions.map((specialty) => (
                    <FormItem
                      key={specialty}
                      css={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "4",
                      }}
                    >
                      <FormControl>
                        <Checkbox
                          disabled={isSubmitting}
                          value={specialty}
                          checked={field.value.includes(specialty)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              field.onChange([
                                ...field.value,
                                specialty,
                              ] as Specialties[]);
                            } else {
                              field.onChange(
                                field.value.filter(
                                  (v: Specialties) => v !== specialty,
                                ),
                              );
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel>{specialty}</FormLabel>
                    </FormItem>
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          disabled={isSubmitting}
          type="submit"
          width="fill"
          variant="brandBlue"
        >
          {isSubmitting && (
            <LoaderCircle width={20} className={css({ animation: "spin" })} />
          )}
          <span>Submit</span>
        </Button>
      </form>
    </ReactForm>
  );
}
